import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Folder,
  FolderOpen,
  MoreHorizontal,
  FolderOpen as FolderOpenIcon,
  Pencil,
  FolderInput,
  Trash2,
} from 'lucide-react';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { cn } from '@/core/lib/utils';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import type { ProjectFolderNodeDto, ProjectFilesViewMode } from '../types';

interface ProjectFolderItemProps {
  folder: ProjectFolderNodeDto;
  viewMode: ProjectFilesViewMode;
  isSelected: boolean;
  onOpen: (id: string) => void;
  onSelect: (id: string, e: React.MouseEvent) => void;
  onRename: (id: string, name: string) => void;
  onMove: (id: string) => void;
  onDelete: (id: string) => void;
}

function FolderActionsMenu({
  folder,
  onOpen,
  onRename,
  onMove,
  onDelete,
}: Pick<ProjectFolderItemProps, 'folder' | 'onOpen' | 'onRename' | 'onMove' | 'onDelete'>) {
  const { t } = useTranslation('projectFiles');
  if (!folder.id || !folder.name) return null;
  const id = folder.id;
  const name = folder.name;

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 text-folder-foreground hover:bg-black/10"
              aria-label={t('folder.actions')}
              data-testid="project-folder-item-menu"
            >
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>{t('folder.actions')}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem onClick={() => onOpen(id)}>
          <FolderOpenIcon className="mr-2 size-4" />
          {t('folder.open')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onRename(id, name)}>
          <Pencil className="mr-2 size-4" />
          {t('folder.rename')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onMove(id)}>
          <FolderInput className="mr-2 size-4" />
          {t('folder.move')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={() => onDelete(id)}
        >
          <Trash2 className="mr-2 size-4" />
          {t('folder.delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ProjectFolderItem({
  folder,
  viewMode,
  isSelected,
  onOpen,
  onSelect,
  onRename,
  onMove,
  onDelete,
}: ProjectFolderItemProps) {
  const { t } = useTranslation('projectFiles');
  const subfolderCount = folder.children?.length ?? 0;
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current || !folder.id) return;
    return dropTargetForElements({
      element: ref.current,
      getData: () => ({ folderId: folder.id }),
      canDrop: ({ source }) => source.data.type === 'file',
      onDragEnter: () => setIsDraggedOver(true),
      onDragLeave: () => setIsDraggedOver(false),
      onDrop: () => setIsDraggedOver(false),
    });
  }, [folder.id]);

  if (viewMode === 'grid') {
    return (
      <div
        className={cn(
          'group/folder flex flex-col cursor-pointer select-none',
          isSelected && 'opacity-90',
        )}
        onClick={(e) => {
          if (e.ctrlKey || e.metaKey || e.shiftKey) {
            e.preventDefault();
            folder.id && onSelect(folder.id, e);
          } else {
            folder.id && onOpen(folder.id);
          }
        }}
        data-testid="project-folder-card"
      >
        {/* Folder tab */}
        <div className="w-[44%] h-5 rounded-t-lg bg-folder-tab shrink-0" />

        {/* Card body */}
        <div
          ref={ref}
          className={cn(
            'flex-1 flex flex-col rounded-tr-lg rounded-b-lg bg-folder-bg p-3 shadow-sm transition-all',
            'group-hover/folder:shadow-md',
            isDraggedOver && 'ring-2 ring-primary shadow-md',
            isSelected && 'ring-2 ring-primary',
          )}
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            {isDraggedOver ? (
              <FolderOpen className="size-6 shrink-0 text-folder-foreground" />
            ) : (
              <Folder className="size-6 shrink-0 text-folder-foreground" />
            )}
            <div
              className="opacity-0 group-hover/folder:opacity-100 transition-opacity -mt-1 -mr-1"
              onClick={(e) => e.stopPropagation()}
            >
              <FolderActionsMenu
                folder={folder}
                onOpen={onOpen}
                onRename={onRename}
                onMove={onMove}
                onDelete={onDelete}
              />
            </div>
          </div>

          <p
            className="flex-1 text-sm font-medium line-clamp-2 leading-snug text-folder-foreground"
            title={folder.name ?? undefined}
          >
            {folder.name}
          </p>

          <div className="mt-2 flex items-center gap-2 min-h-[1.1875rem]">
            {subfolderCount > 0 && (
              <span className="rounded-full px-1.5 py-0.5 text-[0.625rem] leading-none bg-primary/10 text-primary">
                {t('folder.subfolderCount', { count: subfolderCount })}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={cn(
        'group/row flex items-center gap-3 border-b px-4 py-3 transition-colors',
        isDraggedOver ? 'bg-primary/10 ring-inset ring-1 ring-primary' : 'hover:bg-muted/50',
        isSelected && 'bg-primary/5',
      )}
      data-testid="project-folder-item"
    >
      {isDraggedOver ? (
        <FolderOpen className="size-5 shrink-0 text-folder-foreground fill-folder-bg" />
      ) : (
        <Folder className="size-5 shrink-0 text-folder-foreground fill-folder-bg" />
      )}

      <button
        type="button"
        className="min-w-0 flex-1 truncate text-sm font-medium text-left hover:underline"
        onClick={(e) => {
          if (e.ctrlKey || e.metaKey || e.shiftKey) {
            folder.id && onSelect(folder.id, e);
          } else {
            folder.id && onOpen(folder.id);
          }
        }}
        data-testid="project-folder-item-name"
        title={folder.name ?? undefined}
      >
        {folder.name}
      </button>

      <span className="hidden text-xs text-muted-foreground lg:block w-20 shrink-0 text-right">
        {subfolderCount > 0 ? t('folder.subfolderCount', { count: subfolderCount }) : '—'}
      </span>
      <span className="hidden text-xs text-muted-foreground lg:block w-28 shrink-0 truncate">
        —
      </span>
      <span className="hidden text-xs text-muted-foreground md:block w-24 shrink-0 text-right">
        —
      </span>

      <div className="flex w-[84px] shrink-0 items-center justify-end gap-1 opacity-0 group-hover/row:opacity-100 focus-within:opacity-100 transition-opacity">
        <FolderActionsMenu
          folder={folder}
          onOpen={onOpen}
          onRename={onRename}
          onMove={onMove}
          onDelete={onDelete}
        />
      </div>
    </div>
  );
}
